import React, { useMemo, useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/AppShell";
import SymptomsSummaryCard from "../components/SymptomsSummaryCard";
import MoodSummaryCard from "../components/MoodSummaryCard";
import WeeklyProgressCard from "../components/WeeklyProgress";
import { useUserProfile } from "../hooks/useUserProfile";
import StreakBanner from "../components/StreakBanner";
import { useAllWeeksUnlock } from "../hooks/useWeekUnlock";
import { useStreak } from "../hooks/useStreak";
import "./Dashboard.css";

function useWindowWidth(): number {
  const [width, setWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 375
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

function getHeaderHeight(width: number): number {
  if (width >= 1024) return 260;
  if (width >= 768)  return 250;
  if (width >= 510)  return 245;
  if (width >= 480)  return 260;
  if (width >= 360)  return 242;
  return 240;
}

const GPSummaryCard: React.FC<{ unlocked: boolean }> = ({ unlocked }) => {
  const navigate = useNavigate();

  return (
    <section
      className="dashboard-card"
      style={{
        padding: "20px",
        borderRadius: "24px",
        border: unlocked ? "2px solid #C6D8A5" : "1px solid #E8E2DA",
        background: unlocked
          ? "linear-gradient(135deg, #F3F9F7 0%, #FFFFFF 100%)"
          : "#FAFAFA",
        cursor: unlocked ? "pointer" : "default",
      }}
      onClick={unlocked ? () => navigate("/gp-summary") : undefined}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: unlocked ? "#6C7A52" : "#E5E7EB",
            flexShrink: 0,
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={unlocked ? "#fff" : "#9CA3AF"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
            <rect x="9" y="3" width="6" height="4" rx="1" />
            <path d="M9 12h6M9 16h4" />
          </svg>
        </div>
        <div style={{ flex: 1 }}>
          <h3
            style={{
              margin: 0,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              color: unlocked ? "#0C2624" : "#9CA3AF",
            }}
          >
            {unlocked ? "Your GP Summary is Ready" : "GP Summary"}
          </h3>
          <p
            style={{
              margin: "4px 0 0",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: unlocked ? "#6C7A52" : "#9CA3AF",
              lineHeight: 1.4,
            }}
          >
            {unlocked
              ? "Congratulations on your 6-week milestone! Tap to view and share with your doctor."
              : "Available when you complete your 6-week programme."}
          </p>
        </div>
        {unlocked && (
          <div style={{ flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6C7A52" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </div>
        )}
      </div>
    </section>
  );
};

const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();
  const { profile } = useUserProfile();
  const { currentStreak, activeDates, isFirstVisit, loading: streakLoading } = useStreak();
  const { unlockedWeek } = useAllWeeksUnlock();

  const viewportWidth = useWindowWidth();
  const headerHeight  = getHeaderHeight(viewportWidth);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (typeof data.profileImageUrl === "string") {
            setProfileImageUrl(data.profileImageUrl);
          }
        }
      },
      (err) => {
        console.error("Dashboard: could not load profileImageUrl", err);
      }
    );

    return () => unsub();
  }, [user]);

  const displayNameToShow = useMemo(() => {
    if (!user) return "";
    const profileFullName = typeof profile?.fullName === "string" ? profile.fullName : "";
    if (profileFullName.trim()) return profileFullName.trim().split(" ")[0];
    if (user.displayName?.trim()) return user.displayName.trim().split(" ")[0];
    if (user.email) return user.email.split("@")[0];
    return "";
  }, [user, profile]);

  if (loading || isFirstVisit === null || streakLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100%',
        minHeight: '-webkit-fill-available',
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
        pointerEvents: 'all'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (profile?.ftuPath === "antenatal") return <Navigate to="/antenatal-journey" replace />;
  if (profile?.ftuPath === "circumstances") return <Navigate to="/circumstances-support" replace />;

  return (
    <AppShell
      title="Your health dashboard"
      variant="home"
      headerConfig={{
        title: "",
        gradient: "linear-gradient(167.14deg, #B19ABA -2.38%, #EDE4F1 94.45%)",
        height: headerHeight,
        headerExtra: (
          <StreakBanner
            displayName={displayNameToShow}
            photoURL={profileImageUrl}
            viewportWidth={viewportWidth}
            isFirstVisit={isFirstVisit}
            currentStreak={currentStreak}
            activeDates={activeDates}
            streakLoading={streakLoading}
          />
        ),
      }}
      contentTitle={<h1 className="question">How you're feeling today?</h1>}
    >
      <SymptomsSummaryCard userId={user.uid} currentWeek={unlockedWeek} />
      <MoodSummaryCard userId={user.uid} />
      <WeeklyProgressCard />
      <GPSummaryCard unlocked={unlockedWeek >= 6} />
    </AppShell>
  );
};

export default Dashboard;